<template>
  <div :class="[ns.b(), { [ns.m('highlight-current')]: highlightCurrent }]" role="tree">
    <fixed-size-list v-if="isNotEmpty" ref="listRef" :class-name="ns.b('virtual-list')" :data="flattenTree"
      :total="flattenTree.length" :height="height" :item-size="treeNodeSize" :perf-mode="perfMode"
      :scrollbar-always-on="scrollbarAlwaysOn">
      <template #default="{ data, index, style }">
        <z-tree-node :key="data[index].key" :style="style" :node="data[index]" :expanded="data[index].expanded"
          :show-checkbox="showCheckbox" :checked="isChecked(data[index])" :indeterminate="isIndeterminate(data[index])"
          :item-size="treeNodeSize" :disabled="isDisabled(data[index])" :current="isCurrent(data[index])"
          :hidden-expand-icon="isForceHiddenExpandIcon(data[index])" @click="handleNodeClick" @toggle="toggleExpand"
          @check="handleNodeCheck" @drop="handleNodeDrop" />
      </template>
    </fixed-size-list>
    <div v-else :class="ns.e('empty-block')">
      <slot name="empty">
        <span :class="ns.e('empty-text')">
          {{ emptyText ?? t('z.tree.emptyText') }}
        </span>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, provide, useSlots } from 'vue'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { formItemContextKey } from '@zoryn/components/form'
import { FixedSizeList } from '@zoryn/components/virtual-list'
import { useTree } from './composables/useTree'
import ZTreeNode from './tree-node.vue'
import {
  ROOT_TREE_INJECTION_KEY,
  TreeOptionsEnum,
  treeEmits,
} from './virtual-tree'
import { mutable } from '@zoryn/utils'

import type { TreeProps } from './types'

defineOptions({
  name: 'ZTreeV2',
})

const props = withDefaults(defineProps<TreeProps>(), {
  data: () => mutable([]),
  height: 200,
  props: () =>
    mutable({
      children: TreeOptionsEnum.CHILDREN,
      label: TreeOptionsEnum.LABEL,
      disabled: TreeOptionsEnum.DISABLED,
      value: TreeOptionsEnum.KEY,
      class: TreeOptionsEnum.CLASS,
    }),
  defaultCheckedKeys: () => mutable([]),
  defaultExpandedKeys: () => mutable([]),
  indent: 16,
  itemSize: 26,
  expandOnClickNode: true,
  checkOnClickLeaf: true,
  perfMode: true,
})
const emit = defineEmits(treeEmits)

const slots = useSlots()

const treeNodeSize = computed(() => props.itemSize)

provide(ROOT_TREE_INJECTION_KEY, {
  ctx: {
    emit,
    slots,
  },
  props,
  instance: getCurrentInstance()!,
})
provide(formItemContextKey, undefined)
const { t } = useLocale()
const ns = useNamespace('tree')
const {
  flattenTree,
  isNotEmpty,
  toggleExpand,
  isIndeterminate,
  isChecked,
  isDisabled,
  isCurrent,
  isForceHiddenExpandIcon,
  handleNodeClick,
  handleNodeDrop,
  handleNodeCheck,
  // expose
  toggleCheckbox,
  getCurrentNode,
  getCurrentKey,
  setCurrentKey,
  getCheckedKeys,
  getCheckedNodes,
  getHalfCheckedKeys,
  getHalfCheckedNodes,
  setChecked,
  setCheckedKeys,
  filter,
  setData,
  getNode,
  expandNode,
  collapseNode,
  setExpandedKeys,
  scrollToNode,
  scrollTo,
} = useTree(props, emit)

defineExpose({
  toggleCheckbox,
  getCurrentNode,
  getCurrentKey,
  setCurrentKey,
  getCheckedKeys,
  getCheckedNodes,
  getHalfCheckedKeys,
  getHalfCheckedNodes,
  setChecked,
  setCheckedKeys,
  filter,
  setData,
  getNode,
  expandNode,
  collapseNode,
  setExpandedKeys,
  scrollToNode,
  scrollTo,
})
</script>
